"use client"

import { useIsDesktop } from "@/hooks/app/useIsDesktop"
import { useToursCategories } from "@/hooks/tour/useToursCategories"
import { Button, Card, Col, InputNumber, Row, Slider } from "antd"
import { useTETranslation } from "@/hooks/app/useTETranslation"
import { useAppSelector } from "@/lib/redux/hooks"
import { tourActions } from "@/lib/redux/features/tour/reduce"
import { useMemo } from "react"
import ListCategories from "./ListCategories"
import Title from "@/components/common/Typography/Title"
import { useAppDispatch } from "@/lib/redux/hooks"
import ListMonths from "./ListMonths"
import ListDuration from "./ListDuration"
import { FC } from "react"
import { useTranslation } from "react-i18next"

const OtherCategories: FC<{ onChange?: () => void }> = ({ onChange }) => {
  const isDesktop = useIsDesktop()
  const { data: { tourCategories } } = useToursCategories()
  const { t } = useTranslation()
  const tours = useAppSelector(state => state.tour.tours_search.data)
  const priceRange = useAppSelector(state => state.tour.formSearch.priceRange)
  const { getFormatCurrency } = useTETranslation()

  const dispatch = useAppDispatch()

  const priceBounds = useMemo(() => {
    const prices = tours
      .map(tour => parseFloat(tour.priceDefault))
      .filter(price => Number.isFinite(price) && price > 0)
    if (!prices.length) return null
    return [Math.min(...prices), Math.max(...prices)] as [number, number]
  }, [tours])

  const activePriceRange = priceRange || priceBounds

  const tourCategoriesInbound = tourCategories.filter((tour) => tour.tourType === 'DOMESTIC')
  const tourCategoriesOutbound = tourCategories.filter((tour) => tour.tourType !== 'DOMESTIC')

  const renderTitle = (title: any, { shownClear, onClear, ...props }: any) => {
    return (
      <Row {...props} className="flights__list--filter-input--box" justify='space-between' align='middle'>
        <Col>
          <Title level={5} style={{ margin: 0 }}>
            {title}
          </Title>
        </Col>
        {shownClear && (
          <Col>
            <Button
              onClick={onClear}
              type='text'
            >
              {t("common.reset")}
            </Button>
          </Col>
        )}
      </Row>
    )
  }

  return (
    <Row>
      <Col span={24}>
        <Card
          variant={isDesktop ? 'outlined' : 'borderless'}
          size='small'
          className="flights__list--filter tour-filter-sidebar"
          style={isDesktop ? {} : { borderRadius: 0 }}
          styles={{
            body: {
              paddingInline: 0
            },
            header: {
              paddingInline: '1rem',
              minHeight: '4rem'
            }
          }}
        >
          <ListMonths onChange={onChange} />
          <ListDuration onChange={onChange} />
          {!!priceBounds && (
            <>
              {renderTitle(t("tour.price_filter"), {
                style: { marginTop: 15 },
                shownClear: !!priceRange,
                onClear: () => {
                  dispatch(tourActions.setTourPriceRange(undefined))
                  onChange?.()
                },
              })}
              <Row className="flights__list--filter-input--box tour-filter-price">
                <Col span={24}>
                  <Slider
                    range
                    min={priceBounds[0]}
                    max={priceBounds[1]}
                    step={100000}
                    value={activePriceRange || priceBounds}
                    tooltip={{ formatter: val => getFormatCurrency(val || 0) }}
                    onChange={(value) => {
                      if (!Array.isArray(value)) return
                      dispatch(tourActions.setTourPriceRange([value[0], value[1]]))
                    }}
                    onChangeComplete={() => onChange?.()}
                  />
                </Col>
                <Col span={12}>
                  <InputNumber
                    style={{ width: "100%" }}
                    min={priceBounds[0]}
                    max={priceBounds[1]}
                    value={activePriceRange?.[0]}
                    formatter={val => getFormatCurrency(Number(val) || 0)}
                    onChange={(val) => {
                      if (val === null || !activePriceRange) return
                      dispatch(
                        tourActions.setTourPriceRange([Number(val), activePriceRange[1]])
                      )
                    }}
                  />
                </Col>
                <Col span={12}>
                  <InputNumber
                    style={{ width: "100%" }}
                    min={priceBounds[0]}
                    max={priceBounds[1]}
                    value={activePriceRange?.[1]}
                    formatter={val => getFormatCurrency(Number(val) || 0)}
                    onChange={(val) => {
                      if (val === null || !activePriceRange) return
                      dispatch(
                        tourActions.setTourPriceRange([activePriceRange[0], Number(val)])
                      )
                    }}
                  />
                </Col>
              </Row>
            </>
          )}
          {renderTitle(t("tour.hot_inbound"), { marginTop: 15 })}
          <ListCategories
            onChange={onChange}
            tourCategories={tourCategoriesInbound}
          />
          {renderTitle(t("tour.hot_outbound"), { style: { marginTop: 15 } })}
          <ListCategories
            onChange={onChange}
            tourCategories={tourCategoriesOutbound}
          />

        </Card>
      </Col>
    </Row>
  )
}

export default OtherCategories